import React, { useEffect, useState, useRef, createElement } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { CheckCircle2Icon } from 'lucide-react';
const inputClasses = 'w-full py-3 input-minimal-dark text-[15px]';
const labelClasses = 'block text-[10px] font-medium text-white/50 mb-1 tracking-[0.2em] uppercase';
export function Reservations() {
  const {
    t
  } = useLanguage();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [emailjsLoaded, setEmailjsLoaded] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if ((window as any).emailjs) {
      setEmailjsLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
    script.async = true;
    script.onload = () => {
      setEmailjsLoaded(true);
    };
    document.head.appendChild(script);
  }, []);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSending) return;
    const ejs = (window as any).emailjs;
    if (!ejs) {
      alert('El servicio de correo aún se está cargando. Inténtalo en unos segundos.');
      return;
    }
    setIsSending(true);
    ejs.sendForm('service_0vft0jm', 'template_yuqcjng', e.target as HTMLFormElement, 'OZTKwpnwW_ZXrtxX9').then(function () {
      alert('¡Reserva enviada con éxito! Revisa tu correo');
      if (formRef.current) formRef.current.reset();
      setIsSubmitted(true);
      setIsSending(false);
    }, function (error: any) {
      console.error('EmailJS error:', error);
      alert('Error al enviar: ' + (error?.text || error?.message || JSON.stringify(error)));
      setIsSending(false);
    });
  };
  return <section id="reservations" className="py-32 lg:py-44 relative overflow-hidden" data-id="element-239">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full z-0" data-id="element-240">
        <video autoPlay loop muted playsInline className="object-cover w-full h-full opacity-50 scale-105" data-id="element-241">
          <source src="https://videos.pexels.com/video-files/3191572/3191572-uhd_2560_1440_25fps.mp4" type="video/mp4" data-id="element-242" />
        </video>
        <div className="absolute inset-0 bg-navy/85" data-id="element-243" />
      </div>

      {/* Warm ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-sunset-gold/[0.06] rounded-full blur-[120px] pointer-events-none z-0" data-id="element-244"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-terracotta/[0.05] rounded-full blur-[100px] pointer-events-none z-0" data-id="element-245"></div>

      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-10 relative z-10" data-id="element-246">
        <div className="text-center mb-16" data-id="element-247">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex items-center justify-center gap-5 mb-6" data-id="element-248">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-sand/40" data-id="element-249"></div>
            <span className="text-sand-light text-[11px] font-medium tracking-[0.35em] uppercase" data-id="element-250">
              {t.reservations.subtitle}
            </span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-sand/40" data-id="element-251"></div>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1,
          duration: 0.8
        }} className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-white" data-id="element-252">
            {t.reservations.title}
          </motion.h2>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 25
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1]
      }} className="glass-card-dark p-10 md:p-16 rounded-xs" data-id="element-253">
          {isSubmitted ? <div className="text-center py-16" data-id="element-254">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} className="flex justify-center mb-8" data-id="element-255">
                <CheckCircle2Icon className="w-18 h-18 text-sand" data-id="element-256" />
              </motion.div>
              <h3 className="font-serif text-3xl text-white mb-4" data-id="element-257">
                {t.reservations.form.successTitle}
              </h3>
              <p className="text-white/60 mb-10 max-w-md mx-auto leading-relaxed font-light" data-id="element-258">
                {t.reservations.form.successMessage}
              </p>
              <button onClick={() => setIsSubmitted(false)} className="text-sand hover:text-white font-medium transition-colors duration-500 border-b border-sand hover:border-white pb-1 text-sm tracking-wide" data-id="element-259">
                {t.reservations.form.newReservation}
              </button>
            </div> : <form ref={formRef} onSubmit={handleSubmit} className="space-y-10" data-id="element-260">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-id="element-261">
                <div data-id="element-262">
                  <label htmlFor="name" className={labelClasses} data-id="element-263">
                    {t.reservations.form.name}
                  </label>
                  <input type="text" id="name" name="nombre_completo" required className={inputClasses} data-id="element-264" />
                </div>
                <div data-id="element-265">
                  <label htmlFor="email" className={labelClasses} data-id="element-266">
                    {t.reservations.form.email}
                  </label>
                  <input type="email" id="email" name="correo_electronico" required className={inputClasses} data-id="element-267" />
                </div>
                <div data-id="element-268">
                  <label htmlFor="phone" className={labelClasses} data-id="element-269">
                    {t.reservations.form.phone}
                  </label>
                  <input type="tel" id="phone" name="telefono" required className={inputClasses} data-id="element-270" />
                </div>
                <div data-id="element-271">
                  <label htmlFor="guests" className={labelClasses} data-id="element-272">
                    {t.reservations.form.guests}
                  </label>
                  <select id="guests" name="numero_personas" required className={inputClasses + ' appearance-none bg-transparent'} data-id="element-273">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, '10+'].map(num => <option key={num} value={num} className="text-navy" data-id="element-274">
                        {num}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-275">
                  <label htmlFor="date" className={labelClasses} data-id="element-276">
                    {t.reservations.form.date}
                  </label>
                  <input type="date" id="date" name="fecha" required className={inputClasses} data-id="element-277" />
                </div>
                <div data-id="element-278">
                  <label htmlFor="time" className={labelClasses} data-id="element-279">
                    {t.reservations.form.time}
                  </label>
                  <select id="time" name="hora" required className={inputClasses + ' appearance-none bg-transparent'} data-id="element-280">
                    <option value="" className="text-navy" data-id="element-281">
                      --:--
                    </option>
                    <optgroup label="Almuerzo" className="text-navy" data-id="element-282">
                      <option value="13:00" data-id="element-283">13:00</option>
                      <option value="13:30" data-id="element-284">13:30</option>
                      <option value="14:00" data-id="element-285">14:00</option>
                      <option value="14:30" data-id="element-286">14:30</option>
                      <option value="15:00" data-id="element-287">15:00</option>
                    </optgroup>
                    <optgroup label="Cena" className="text-navy" data-id="element-288">
                      <option value="20:00" data-id="element-289">20:00</option>
                      <option value="20:30" data-id="element-290">20:30</option>
                      <option value="21:00" data-id="element-291">21:00</option>
                      <option value="21:30" data-id="element-292">21:30</option>
                      <option value="22:00" data-id="element-293">22:00</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <div data-id="element-294">
                <label htmlFor="requests" className={labelClasses} data-id="element-295">
                  {t.reservations.form.requests}
                </label>
                <textarea id="requests" name="peticiones_especiales" rows={2} className={inputClasses + ' resize-none'} data-id="element-296"></textarea>
              </div>

              <div className="pt-4" data-id="element-297">
                <button type="submit" disabled={isSending} className={`w-full bg-sand/90 backdrop-blur-sm hover:bg-sand text-navy font-medium py-4.5 transition-all duration-700 ease-elegant tracking-[0.2em] uppercase text-[11px] hover:shadow-warm hover:scale-[1.01] ${isSending ? 'opacity-60 cursor-not-allowed hover:scale-100' : ''}`} data-id="element-298">
                  {isSending ? 'Enviando...' : t.reservations.form.submit}
                </button>
              </div>
            </form>}
        </motion.div>
      </div>
    </section>;
}